import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trash2, Undo2, ScanBarcode, AlertTriangle, CheckCircle2, CalendarX, History, MessageSquare, BarChart3, CloudOff } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CalendarDays } from 'lucide-react';
import { sitesAPI, lossesAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import LossesSynthesis from '@/pages/losses/LossesSynthesis';

const todayIso = () => new Date().toISOString().slice(0, 10);

const CL_SITE = { id: 'CL_SOCOARMES', name: 'Concept Lab — Socoarmes' };

const makeClientId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function beep(ok = true) {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = ok ? 880 : 220;
        gain.gain.value = 0.15;
        osc.start();
        osc.stop(ctx.currentTime + (ok ? 0.12 : 0.3));
    } catch (e) { /* silent */ }
}

export default function LossesPage() {
    const { user } = useAuth();
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState(null);
    const [view, setView] = useState('saisie'); // saisie | synthese
    const [catalog, setCatalog] = useState([]);
    const [entries, setEntries] = useState(null);
    const [lossType, setLossType] = useState('dlc');
    const [entryDate, setEntryDate] = useState(todayIso());
    const [noteText, setNoteText] = useState('');
    const [noteMeta, setNoteMeta] = useState(null);
    const [noteStatus, setNoteStatus] = useState('idle'); // idle | dirty | saving | saved
    const [feedback, setFeedback] = useState(null); // {ok, message, sub}
    const feedbackTimer = useRef(null);
    const scanBuffer = useRef('');
    const scanTimer = useRef(null);

    const isPrivileged = ['admin', 'supervisor'].includes(user?.role);
    const perms = user?.permissions || [];
    const canFE = isPrivileged || perms.includes('losses_entry');
    const canCL = isPrivileged || perms.includes('losses_cl_entry');

    useEffect(() => {
        if (!canFE) {
            setSites([]);
            if (canCL) setSiteId(CL_SITE.id);
            return;
        }
        sitesAPI.getAll().then(r => {
            const fe = r.data.filter(s => s.name?.startsWith('FE ') && !/si[eè]ge/i.test(s.name));
            const allowed = user?.allowed_sites || [];
            const restricted = allowed.length > 0 ? fe.filter(s => allowed.includes(s.id)) : fe;
            const list = restricted.length > 0 ? restricted : fe;
            setSites(list);
            const dillon = list.find(s => s.name === 'FE Dillon');
            setSiteId(allowed.length > 0 ? list[0]?.id : (dillon?.id || list[0]?.id));
        }).catch(() => toast.error('Erreur de chargement des sites'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadEntries = useCallback(() => {
        if (!siteId || siteId === 'ALL') return;
        lossesAPI.list({ site_id: siteId, date: entryDate }).then(r => setEntries(r.data)).catch(() => {});
    }, [siteId, entryDate]);

    // One-time migration: send any entries left in the removed offline queue,
    // then delete the key (backend dedupes on client_id).
    useEffect(() => {
        let legacy = [];
        try { legacy = JSON.parse(localStorage.getItem('losses_offline_queue_v1')) || []; } catch { /* ignore */ }
        if (legacy.length === 0) { localStorage.removeItem('losses_offline_queue_v1'); return; }
        Promise.allSettled(legacy.map(item => lossesAPI.create(item))).then(results => {
            if (results.every(r => r.status === 'fulfilled')) {
                localStorage.removeItem('losses_offline_queue_v1');
                loadEntries();
            }
        });
    }, [loadEntries]);

    useEffect(() => {
        if (!siteId || siteId === 'ALL') return;
        lossesAPI.catalog({ site_id: siteId })
            .then(r => setCatalog(r.data))
            .catch(() => setCatalog([]));
        loadEntries();
    }, [siteId, loadEntries]);

    useEffect(() => {
        if (!siteId || siteId === 'ALL') return;
        lossesAPI.getNote({ site_id: siteId, date: entryDate }).then(r => {
            setNoteText(r.data.text || '');
            setNoteMeta(r.data);
            setNoteStatus('idle');
        }).catch(() => {});
    }, [siteId, entryDate]);

    const saveNote = async () => {
        if (noteStatus !== 'dirty' || !siteId) return;
        setNoteStatus('saving');
        try {
            await lossesAPI.saveNote({ site_id: siteId, date: entryDate, text: noteText, updated_by: user?.username });
            setNoteStatus('saved');
            setNoteMeta(m => ({ ...(m || {}), updated_by: user?.username }));
        } catch (e) {
            setNoteStatus('dirty');
            toast.error("Erreur d'enregistrement du commentaire");
        }
    };

    const showFeedback = (fb) => {
        setFeedback(fb);
        if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
        feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
    };

    const currentType = () => lossType;

    const recordLoss = async ({ libelle, ean, unit_price, source }) => {
        const type = currentType();
        const payload = {
            client_id: makeClientId(),
            site_id: siteId,
            site_name: siteId === CL_SITE.id ? CL_SITE.name : sites.find(s => s.id === siteId)?.name,
            libelle,
            ean: ean || null,
            type,
            qty: 1,
            unit_price: unit_price ?? null,
            source,
            created_by: user?.username,
            date: entryDate,
            time: new Date().toTimeString().slice(0, 5),
        };
        // Direct save: the page works online like the rest of the app
        try {
            await lossesAPI.create(payload);
            beep(true);
            showFeedback({
                ok: true,
                message: libelle,
                sub: type === 'demarque' ? 'Perte démarque enregistrée'
                    : type === 'demarque_accident' ? 'Perte démarque accident enregistrée'
                    : 'Perte du jour enregistrée',
            });
            loadEntries();
        } catch (e) {
            beep(false);
            showFeedback({ ok: false, message: libelle, sub: "Échec de l'enregistrement — vérifiez la connexion et réessayez" });
        }
    };

    const handleScan = async (code) => {
        if (!siteId) return;
        try {
            const r = await lossesAPI.resolve({ ean: code, site_id: siteId });
            if (r.data.found) {
                await recordLoss({ libelle: r.data.libelle, ean: code, unit_price: r.data.unit_price, source: 'scan' });
            } else if (r.data.reason === 'demarque_label') {
                beep(false);
                showFeedback({ ok: false, message: 'Étiquette démarque scannée', sub: 'Choisis le produit dans la grille ci-dessous 👇' });
            } else {
                beep(false);
                showFeedback({ ok: false, message: `EAN inconnu : ${code}`, sub: 'Choisis le produit dans la grille ci-dessous 👇' });
            }
        } catch (e) {
            beep(false);
            toast.error('Erreur lors de la résolution du code');
        }
    };

    // Global scanner listener: scanners type digits fast then Enter
    useEffect(() => {
        const onKey = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.key === 'Enter') {
                if (scanBuffer.current.length >= 8) {
                    handleScan(scanBuffer.current);
                }
                scanBuffer.current = '';
                return;
            }
            if (/^[0-9]$/.test(e.key)) {
                scanBuffer.current += e.key;
                if (scanTimer.current) clearTimeout(scanTimer.current);
                scanTimer.current = setTimeout(() => { scanBuffer.current = ''; }, 400);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line
    }, [siteId, sites, lossType, entryDate]);

    const handleUndo = async (id) => {
        try {
            await lossesAPI.remove(id);
            toast.success('Perte annulée');
            loadEntries();
        } catch (e) {
            toast.error("Erreur d'annulation");
        }
    };

    const effectiveType = currentType();

    const tileGroups = useMemo(() => {
        const groups = [];
        let current = null;
        for (const p of catalog) {
            if (!current || current.famille !== p.famille) {
                current = { famille: p.famille, items: [] };
                groups.push(current);
            }
            current.items.push(p);
        }
        return groups;
    }, [catalog]);

    const groupedEntries = useMemo(() => {
        const map = new Map();
        // rows are already sorted by created_at desc
        for (const r of (entries?.rows || [])) {
            const key = `${r.libelle}||${r.type}`;
            if (!map.has(key)) {
                map.set(key, { libelle: r.libelle, type: r.type, qty: 0, lastTime: r.time, lastId: r.id });
            }
            map.get(key).qty += r.qty;
        }
        return Array.from(map.values());
    }, [entries]);

    return (
        <div className="space-y-3" data-testid="losses-page">
            {/* Header: site + type */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap gap-1.5" data-testid="losses-site-chips">
                    {sites.map(s => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => setSiteId(s.id)}
                            className={`px-3 h-8 rounded-full text-xs font-semibold border ${siteId === s.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                            data-testid={`losses-site-${s.name.replace('FE ', '').toLowerCase()}`}
                        >{s.name.replace('FE ', '')}</button>
                    ))}
                    {canCL && (
                        <button
                            type="button"
                            onClick={() => setSiteId(CL_SITE.id)}
                            className={`px-3 h-8 rounded-full text-xs font-semibold border ${siteId === CL_SITE.id ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-violet-700 border-violet-300'}`}
                            data-testid="losses-site-socoarmes"
                        >Socoarmes · Concept Lab</button>
                    )}
                    {isPrivileged && (
                        <button
                            type="button"
                            onClick={() => { setSiteId('ALL'); setView('synthese'); }}
                            className={`px-3 h-8 rounded-full text-xs font-semibold border ${siteId === 'ALL' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-orange-600 border-orange-300'}`}
                            data-testid="losses-site-fuji-global"
                        >Fuji Express</button>
                    )}
                </div>
                <div className="flex-1" />
                <div className="inline-flex bg-slate-200 rounded-lg p-0.5 gap-0.5" data-testid="losses-view-toggle">
                    <button type="button" onClick={() => { setView('saisie'); if (siteId === 'ALL') setSiteId(sites[0]?.id || (canCL ? CL_SITE.id : null)); }}
                        className={`px-3 h-8 rounded-md text-xs font-bold ${view === 'saisie' ? 'bg-white shadow' : 'text-slate-600'}`}
                        data-testid="losses-view-saisie">Saisie</button>
                    <button type="button" onClick={() => setView('synthese')}
                        className={`px-3 h-8 rounded-md text-xs font-bold flex items-center gap-1 ${view === 'synthese' ? 'bg-white shadow' : 'text-slate-600'}`}
                        data-testid="losses-view-synthese"><BarChart3 className="w-3.5 h-3.5" /> Synthèse</button>
                </div>
                {view === 'saisie' && (
                <div className="inline-flex bg-slate-200 rounded-lg p-0.5 gap-0.5" data-testid="losses-type-toggle">
                    <button
                        type="button"
                        onClick={() => setLossType('dlc')}
                        className={`px-4 py-2.5 rounded-md text-sm font-bold flex items-center gap-1.5 ${effectiveType === 'dlc' ? 'bg-yellow-400 text-slate-900' : 'text-slate-600'}`}
                        data-testid="losses-type-dlc"
                    ><CalendarX className="w-4 h-4" />DLC</button>
                    <button
                        type="button"
                        onClick={() => setLossType('veille')}
                        className={`px-4 py-2.5 rounded-md text-sm font-bold flex items-center gap-1.5 ${effectiveType === 'veille' ? 'bg-orange-500 text-white' : 'text-slate-600'}`}
                        data-testid="losses-type-veille"
                    ><History className="w-4 h-4" />Veille</button>
                    <button
                        type="button"
                        onClick={() => setLossType('jour')}
                        className={`px-4 py-2.5 rounded-md text-sm font-bold flex items-center gap-1.5 ${effectiveType === 'jour' ? 'bg-red-500 text-white' : 'text-slate-600'}`}
                        data-testid="losses-type-jour"
                    ><AlertTriangle className="w-4 h-4" />Du jour</button>
                </div>
                )}
            </div>

            {view === 'synthese' && (
                <LossesSynthesis siteId={siteId} />
            )}

            {view === 'saisie' && (<>
            {/* Entry date */}
            <div className="flex flex-wrap items-center gap-2" data-testid="losses-date-row">
                <CalendarDays className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-semibold text-slate-500">Date de saisie :</span>
                <Input
                    type="date"
                    value={entryDate}
                    max={todayIso()}
                    onChange={e => setEntryDate(e.target.value || todayIso())}
                    className="h-8 w-[150px] text-xs"
                    data-testid="losses-entry-date"
                />
                <button
                    type="button"
                    onClick={() => setEntryDate(todayIso())}
                    className={`px-2.5 h-7 rounded border text-[11px] font-semibold ${entryDate === todayIso() ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    data-testid="losses-date-today"
                >Aujourd'hui</button>
                <button
                    type="button"
                    onClick={() => {
                        const d = new Date(); d.setDate(d.getDate() - 1);
                        setEntryDate(d.toISOString().slice(0, 10));
                    }}
                    className="px-2.5 h-7 rounded border text-[11px] font-semibold bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                    data-testid="losses-date-yesterday"
                >Hier</button>
                {entryDate !== todayIso() && (
                    <Badge className="bg-blue-100 text-blue-700 text-[10px] animate-pulse" data-testid="losses-retro-badge">
                        Saisie rétroactive au {entryDate.slice(8)}/{entryDate.slice(5, 7)}/{entryDate.slice(0, 4)}
                    </Badge>
                )}
            </div>

            {/* Feedback zone */}
            <Card className={`transition-colors ${feedback ? (feedback.ok ? 'bg-emerald-500 border-emerald-500' : 'bg-amber-500 border-amber-500') : 'bg-slate-50'}`} data-testid="losses-feedback">
                <CardContent className="py-5 flex items-center gap-4">
                    {feedback ? (
                        <>
                            {feedback.ok
                                ? <CheckCircle2 className="w-10 h-10 text-white shrink-0" />
                                : <AlertTriangle className="w-10 h-10 text-white shrink-0" />}
                            <div>
                                <div className="text-xl font-bold text-white" data-testid="losses-feedback-message">{feedback.message}</div>
                                <div className="text-sm text-white/80">{feedback.sub}</div>
                            </div>
                        </>
                    ) : (
                        <>
                            <ScanBarcode className={`w-10 h-10 shrink-0 ${effectiveType === 'dlc' ? 'text-yellow-400' : effectiveType === 'veille' ? 'text-orange-400' : 'text-red-300'}`} />
                            <div>
                                <div className="text-lg font-bold text-slate-500">
                                    {effectiveType === 'dlc'
                                        ? 'Pertes DLC — barquettes de l\'avant-veille en fin de vie (démarquées ou non)'
                                        : effectiveType === 'veille'
                                        ? 'Pertes VEILLE — barquettes de la veille, accident de parcours (démarquées ou non)'
                                        : 'Pertes DU JOUR — barquettes produites aujourd\'hui'}
                                </div>
                                <div className="text-xs text-slate-400">1 tap sur un produit = 1 barquette perdue. (Douchette EAN aussi compatible si branchée.)</div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {/* Product tiles grouped by family (identical layout on both screens) */}
                <div className="lg:col-span-2 space-y-3 lg:max-h-[calc(100vh-290px)] lg:overflow-y-auto lg:pr-2" data-testid="losses-tiles">
                    {tileGroups.map((g) => (
                        <div key={g.famille}>
                            <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mb-1.5" data-testid={`losses-family-${g.famille.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                {g.famille}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                                {g.items.map((p) => (
                                    <button
                                        key={p.libelle}
                                        type="button"
                                        onClick={() => recordLoss({ libelle: p.libelle, unit_price: p.ref_price, source: 'tile' })}
                                        className={`min-h-[76px] px-3 py-2 rounded-lg border text-left active:scale-95 transition-transform ${effectiveType === 'dlc' ? 'bg-yellow-300 border-yellow-400 hover:bg-yellow-400' : effectiveType === 'veille' ? 'bg-orange-200 border-orange-400 hover:bg-orange-300' : 'bg-white border-red-300 hover:border-red-500 hover:bg-red-50'}`}
                                        data-testid={`losses-tile-${p.libelle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                    >
                                        <div className="text-sm font-bold text-slate-800 leading-tight">{p.libelle}</div>
                                        <div className={`text-[11px] mt-0.5 ${effectiveType === 'dlc' ? 'text-yellow-800' : effectiveType === 'veille' ? 'text-orange-800' : 'text-slate-400'}`}>{formatCurrency(p.ref_price)}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                    {catalog.length === 0 && (
                        <div className="py-8 text-center text-slate-400 text-sm">
                            Aucun produit connu pour ce site (les tuiles proviennent des données Vitrine).
                        </div>
                    )}
                </div>

                {/* Today's list + comment */}
                <div className="space-y-3">
                <Card className="h-fit">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Trash2 className="w-4 h-4 text-slate-400" />
                            {entryDate === todayIso() ? 'Pertes du jour' : `Pertes du ${entryDate.slice(8)}/${entryDate.slice(5, 7)}`}
                            {entries && (
                                <Badge variant="outline" className="text-[10px]" data-testid="losses-today-count">
                                    {entries.totals.count} · {formatCurrency(entries.totals.value)}
                                </Badge>
                            )}
                        </CardTitle>
                        {entries && entries.totals.count > 0 && (
                            <p className="text-[10px] text-slate-400">
                                {entries.totals.dlc || 0} DLC · {entries.totals.veille || 0} veille · {entries.totals.jour || 0} du jour
                            </p>
                        )}
                    </CardHeader>
                    <CardContent className="p-0">
                        {groupedEntries.length === 0 ? (
                            <div className="py-6 text-center text-slate-400 text-xs">Aucune perte enregistrée aujourd'hui.</div>
                        ) : (
                            <table className="w-full text-[11px]" data-testid="losses-today-table">
                                <tbody>
                                    {groupedEntries.map((g) => (
                                        <tr key={`${g.libelle}-${g.type}-${g.pending ? 'p' : 's'}`} className={`border-b border-slate-100 ${g.pending ? 'bg-amber-50' : 'odd:bg-white even:bg-slate-50'}`}>
                                            <td className="px-2 py-1 tabular-nums text-slate-400 w-12">
                                                {g.pending ? <CloudOff className="w-3.5 h-3.5 text-amber-500" title="En attente de transmission" /> : g.lastTime}
                                            </td>
                                            <td className="px-2 py-1 font-medium">{g.libelle}</td>
                                            <td className="px-2 py-1 w-10 text-center">
                                                <span className="font-bold tabular-nums" data-testid={`losses-qty-${g.libelle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${g.type}`}>×{Math.round(g.qty)}</span>
                                            </td>
                                            <td className="px-2 py-1 w-16 text-center">
                                                {g.type === 'dlc'
                                                    ? <Badge className="bg-yellow-100 text-yellow-800 text-[9px] px-1.5">DLC</Badge>
                                                    : g.type === 'veille'
                                                    ? <Badge className="bg-orange-100 text-orange-700 text-[9px] px-1.5">Veille</Badge>
                                                    : <Badge className="bg-red-100 text-red-700 text-[9px] px-1.5">Jour</Badge>}
                                            </td>
                                            <td className="px-2 py-1 w-14 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleUndo(g.lastId)}
                                                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-red-200 bg-red-50 text-red-500 active:bg-red-100"
                                                    title="Retirer 1 barquette (la dernière saisie)"
                                                    data-testid={`losses-undo-${g.lastId}`}
                                                ><Undo2 className="w-5 h-5" /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </CardContent>
                </Card>

                {/* Day comment */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-slate-400" />
                            Commentaire du {entryDate.slice(8)}/{entryDate.slice(5, 7)}
                            {noteStatus === 'saving' && <span className="text-[10px] text-slate-400 font-normal">enregistrement...</span>}
                            {noteStatus === 'saved' && <span className="text-[10px] text-emerald-600 font-normal">✓ enregistré</span>}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <Textarea
                            value={noteText}
                            onChange={e => { setNoteText(e.target.value); setNoteStatus('dirty'); }}
                            onBlur={saveNote}
                            placeholder="Ex : coupure de courant vitrine, casse en livraison, forte chaleur, animation Carrefour..."
                            className="min-h-[90px] text-xs"
                            data-testid="losses-note-textarea"
                        />
                        {noteMeta?.updated_by && (
                            <p className="text-[10px] text-slate-400 mt-1">Dernière modification : {noteMeta.updated_by}</p>
                        )}
                    </CardContent>
                </Card>
                </div>
            </div>
            </>)}
        </div>
    );
}
